import { onMounted, onUnmounted, ref, watch } from 'vue'
import { newTrack, registerRumTransformer } from '@/track'
import { useUserStore } from '@/stores'
import { genPlayTraceId } from '@/utils/playTraceId'

registerRumTransformer(
  name => name.startsWith('game_play_point_'),
  ({ en, kv }) => ({
    type: kv.content_id || 'event',
    name: en,
    group: 'game_play_points',
    value: kv.cost_ms,
    properties: {
      ...kv,
      chapter_id: kv.chapter_id ?? null,
      chapter_no: kv.chapter_no ?? null,
      chapter_chat_id: kv.chapter_chat_id ?? null,
    },
  }),
)

export function trackGamePaymentPoint(
  pointType: 'open_pay_panel' | 'click_pay_goods' | 'pay_success',
  params: Record<string, any>,
) {
  newTrack(`game_play_point_${pointType}`, params)
}

export function useGamePlayPoint(contentId: string) {
  const userStore = useUserStore()
  const playTraceId = ref('')

  // 只累计页面可见时间，排除切后台/锁屏的时长
  let visibleElapsed = 0
  let visibleSince = document.hidden ? null : performance.now()

  function handleVisibilityChange() {
    if (document.hidden) {
      if (visibleSince !== null) {
        visibleElapsed += performance.now() - visibleSince
        visibleSince = null
      }
    }
    else {
      visibleSince = performance.now()
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)

  function getVisibleMs() {
    const extra = visibleSince !== null ? performance.now() - visibleSince : 0
    return Math.round(visibleElapsed + extra)
  }

  // cost_ms 在事件发生时已经计算好，等 memberId 有值后再 flush
  const pendingEvents: Array<{ name: string; params: Record<string, any> }> = []

  function trackOrDefer(name: string, params: Record<string, any>) {
    if (userStore.memberId)
      newTrack(name, params)

    else
      pendingEvents.push({ name, params })
  }

  const stopWatch = watch(
    () => userStore.memberId,
    (memberId) => {
      if (memberId && pendingEvents.length > 0) {
        for (const { name, params } of pendingEvents.splice(0))
          newTrack(name, params)
      }
    },
  )

  function resetPlayTraceId() {
    playTraceId.value = genPlayTraceId()
    sessionStorage.setItem('game_play_trace_id', playTraceId.value)
  }

  function handlePageShow(event: PageTransitionEvent) {
    if (event.persisted)
      resetPlayTraceId()
  }

  window.addEventListener('pageshow', handlePageShow)

  onMounted(() => {
    resetPlayTraceId()

    const loginCostMs = sessionStorage.getItem('game_login_cost_ms')
    if (loginCostMs) {
      const costMs = Number.parseInt(loginCostMs)
      trackOrDefer('game_play_point_login', {
        cost_ms: costMs,
        content_id: contentId,
        play_trace_id: playTraceId.value,
      })
      sessionStorage.removeItem('game_login_cost_ms')
    }
  })

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('pageshow', handlePageShow)
    sessionStorage.removeItem('game_play_trace_id')
    stopWatch()
  })

  function trackContentLoaded() {
    const costMs = getVisibleMs()
    trackOrDefer('game_play_point_detail_page_loaded', {
      cost_ms: costMs,
      content_id: contentId,
      play_trace_id: playTraceId.value,
    })
  }

  function trackStartGame() {
    const costMs = getVisibleMs()
    newTrack('game_play_point_start_game', {
      cost_ms: costMs,
      content_id: contentId,
      play_trace_id: playTraceId.value,
    })
  }

  function trackGameEngineLoaded(costMs: number) {
    trackOrDefer('game_play_point_game_engine_preloaded', {
      cost_ms: costMs,
      content_id: contentId,
      play_trace_id: playTraceId.value,
    })
  }

  return { playTraceId, trackContentLoaded, trackStartGame, trackGameEngineLoaded }
}
